import { v4 as uuidv4 } from 'uuid';

// crypto.randomUUID() needs Chrome/Android System WebView 92+, but the Capacitor
// app ships with minSdkVersion 24 and runs on whatever WebView the device has, so
// on old installs the call throws "crypto.randomUUID is not a function" (WIPE-1312,
// WIPE-1338). Guarding each call site individually leaves the next one exposed —
// including calls inside dependencies, which we can't patch. Install the missing
// method once instead so the whole class is closed.

type Uuid = `${string}-${string}-${string}-${string}-${string}`;

// uuid's v4() takes its own crypto.randomUUID fast path whenever it is called with
// no options — which, once this shim is installed, is a call straight back into
// itself. Passing an (empty) options object is what forces the getRandomValues
// implementation; a bare uuidv4() here would recurse until the stack blew.
const randomUuid = (): Uuid => uuidv4({}) as Uuid;

/**
 * Defines globalThis.crypto.randomUUID when the WebView lacks it, returning whether
 * it installed.
 *
 * No-ops when randomUUID already exists, and when getRandomValues is also missing
 * (no crypto at all, or a non-secure context): uuid falls back to nothing else, and
 * failing here keeps the original TypeError rather than replacing it with a less
 * obvious one from inside the shim.
 */
export function installRandomUuidShim(): boolean {
  const c: Crypto | undefined = globalThis.crypto;
  if (!c || typeof c.getRandomValues !== 'function') return false;
  if (typeof c.randomUUID === 'function') return false;

  try {
    Object.defineProperty(c, 'randomUUID', {
      value: randomUuid,
      writable: true,
      configurable: true,
      enumerable: false,
    });
  } catch {
    return false;
  }
  return true;
}

// Runs on import; main.tsx imports this module first for the side effect alone.
installRandomUuidShim();
